import React, { Fragment, createContext, useContext, useRef } from 'react';
import styles from './ReportWizard.module.scss';
import { isEmpty } from 'lodash';
import {
  ReportValue,
  LocationAreaType,
  ApiError,
  GeneralInformationValueType
} from '../../../Models';
import IncidentNumberView from './IncidentNumberView/IncidentNumberView';
import { ContactFormView } from './ContactFormView/ContactFormView';
import ReportSubmittedView from './ReportSubmittedView/ReportSubmittedView';
import {
  getReportsFromIntegration,
  saveAmendReport,
  saveReport,
  SaveReportResponse
} from '../../../Services/ReportService';
import Loader from '../../../Components/Loader/Loader';
import CancelModal from './CancelModal/CancelModal';
import { useHistory, useLocation } from 'react-router-dom';
import { IncidentNumberModal } from '../../../Components/IncidentNumberModal/IncidentNumberModal';
import {
  UserContext,
  AppOptionsContext,
  GeoLocationCoords
} from 'ui-smartforce-settings';
import { StateConfigContext } from '../../../Context/StateConfigContext';
import { handleError, getCoordsAreas } from '../../../Helpers';
import { MainAssistantContext } from '../Main';
import {
  clearDraftReport,
  getDraftReport,
  hasDraftReport,
  setDraftReport
} from '../../../Services/DraftReportService';
import ContinueReportModal from './ContinueReportModal/ContinueReportModal';
import { dispatchMainAlertEvent } from '../../../Events/MainAlertEvent';
import { IntegrationReportsContext } from '../../../Context/IntegrationReportsContext';
import { OnlineStatusContext } from '../../../Context/OnlineStatusContext';

export const DeviceLocationContext = createContext<
  GeoLocationCoords | undefined
>(undefined);

const ReportWizard = (): React.ReactElement<{}> => {
  const deviceDate = React.useRef<string>();
  const history = useHistory();
  const location = useLocation();
  const { setShowAssistant } = useContext(MainAssistantContext);
  const { setIntegrationReports } = useContext(IntegrationReportsContext);
  const { isOnline } = useContext(OnlineStatusContext);
  const { areas } = useContext(AppOptionsContext);
  const { stateConfig } = useContext(StateConfigContext);
  const { user } = React.useContext(UserContext);
  const [step, setStep] = React.useState<number>(1);
  const [incidentNumber, setIncidentNumber] = React.useState<string>('');
  const [caseNumber, setCaseNumber] = React.useState<string>('');
  const [deviceLocation, setDeviceLocation] =
    React.useState<GeoLocationCoords>();
  const [deviceDuration, setDeviceDuration] = React.useState<number>(0);
  const [isCancelModalOpen, setCancelIsModalOpen] =
    React.useState<boolean>(false);
  const [isContinueReportModalOpen, setContinueReportIsModalOpen] =
    React.useState<boolean>(false);
  const draftReport = useRef<ReportValue>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isSubmitted, setIsSubmitted] = React.useState<boolean>(false);
  const [isIncidentNumberModalOpen, setIsIncidentNumberModalOpen] =
    React.useState<boolean>(false);
  const [submittedForm, setSubmittedForm] = React.useState<ReportValue>();
  const [isSessionExpire, setIsSessionExpire] = React.useState<boolean>(false);

  const [generalFormAreas, setGeneralFormAreas] = React.useState<
    LocationAreaType[]
  >([]);

  React.useEffect(() => {
    return () => {
      setShowAssistant(true);
    };
  }, [setShowAssistant]);

  React.useEffect(() => {
    if (deviceLocation) {
      setGeneralFormAreas(getCoordsAreas(deviceLocation, areas));
    }
  }, [areas, deviceLocation]);

  React.useEffect(() => {
    let isSubscribed: boolean = true;

    //Set timer for device duration
    const timer = setInterval(
      () =>
        isSubscribed && setDeviceDuration((duration: number) => duration + 1),
      60000
    );

    //Check for coords for device location
    if (navigator.geolocation) {
      const onLocationError = (): void =>
        console.error('ReportWizard::Location', 'Cant get browser geolocation');

      const onLocationSuccess = (pos: GeolocationPosition): void => {
        const deviceLocation: GeoLocationCoords = {
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude
        };

        isSubscribed && setDeviceLocation(deviceLocation);
      };

      navigator.geolocation.getCurrentPosition(
        onLocationSuccess,
        onLocationError,
        {
          enableHighAccuracy: true
        }
      );
    } else {
      console.error('ReportWizard::Location', 'Cant get browser geolocation');
    }

    //Cleanup
    return () => {
      isSubscribed = false;
      clearInterval(timer);
    };
  }, [history]);

  React.useEffect(() => {
    const draftReportSaved = getDraftReport();

    if (draftReportSaved) {
      draftReport.current = draftReportSaved;
      setContinueReportIsModalOpen(true);
    }

    //Clean Up function
    const showMessage = () => {
      if (hasDraftReport()) {
        dispatchMainAlertEvent({
          message: 'The report was saved as a draft.'
        });
      }
    };

    return () => {
      //set Timeout to avoid show message before Draft report was deleted
      setTimeout(() => {
        showMessage();
      }, 100);
    };
  }, []);

  React.useEffect(() => {
    const updateIntegrationReports = async () => {
      if (isOnline) {
        const integrationReports: ReportValue[] =
          await getReportsFromIntegration();
        setIntegrationReports(integrationReports);
      } else {
        setIntegrationReports([]);
      }
    };

    updateIntegrationReports();
  }, [isOnline, setIntegrationReports]);

  const addDraftGeneralInformationForm = (
    form: ReportValue,
    incidentNumber: string,
    caseNumber: string,
    isEmpty?: boolean
  ): ReportValue => {
    const newForm = { ...form };
    newForm.general_information.incident_number = incidentNumber;
    newForm.general_information.device = {
      coords: deviceLocation,
      duration: deviceDuration,
      date: deviceDate.current as string
    };

    if (isEmpty)
      newForm.general_information.state_related.to_connect_reports.event_identifier =
        caseNumber;

    return newForm;
  };

  const saveFormDraftReport = (
    action: 'leave' | 'continue',
    incidentNumber: string,
    caseNumber: string,
    report?: ReportValue
  ) => {
    const emptyFormValue: ReportValue = {
      general_information: {
        type: '',
        state_related: {
          to_connect_reports: {
            event_identifier: ''
          }
        },
        location: { address: { full: '' }, type: '' }
      },
      citizens: []
    };

    const newForm: ReportValue = report || emptyFormValue;

    switch (action) {
      case 'continue':
        setDraftReport(
          addDraftGeneralInformationForm(
            newForm,
            incidentNumber,
            caseNumber,
            true
          )
        );
        return;
      case 'leave':
        if (!isEmpty(incidentNumber) && !isEmpty(caseNumber)) {
          setDraftReport(
            addDraftGeneralInformationForm(
              newForm,
              incidentNumber,
              caseNumber,
              true
            )
          );
        } else {
          if (location.pathname !== history.location.pathname) {
            deleteDraftReport();
          }
        }
        return;
    }
  };

  const onIncidentContinue = async (
    incidentNumber: string,
    caseNumber: string,
    selectedReport?: ReportValue
  ) => {
    if (selectedReport) {
      draftReport.current = selectedReport;
      saveDraftReport(selectedReport);
      setIncidentNumber(
        selectedReport.general_information.incident_number as string
      );
      setCaseNumber(
        selectedReport.general_information.state_related.to_connect_reports
          .event_identifier as string
      );
    } else {
      //Original code flow
      setDeviceDuration(0);
      deviceDate.current = new Date().toISOString();
      setIncidentNumber(incidentNumber);
      setCaseNumber(caseNumber);
      saveFormDraftReport('continue', incidentNumber, caseNumber);
    }

    setStep(2);
    setShowAssistant(false);
  };

  const onIncidentNumberCleanUp = (
    incidentNumber: string,
    caseNumber: string,
    report?: ReportValue
  ) => {
    if (!report) {
      setIncidentNumber(incidentNumber);
      setCaseNumber(caseNumber);
    } else {
      setIncidentNumber(report.general_information.incident_number as string);
      setCaseNumber(
        report.general_information.state_related.to_connect_reports
          .event_identifier as string
      );
    }

    saveFormDraftReport('leave', incidentNumber, caseNumber, report);
  };

  const saveDraftReport = (contactValue: ReportValue) => {
    const newForm: ReportValue = { ...contactValue };
    setDraftReport(
      addDraftGeneralInformationForm(newForm, incidentNumber, caseNumber, false)
    );
  };

  const deleteDraftReport = () => {
    clearDraftReport();
    draftReport.current = undefined;
  };

  const onDiscardDraftReport = () => {
    deleteDraftReport();
    setContinueReportIsModalOpen(false);
  };

  const onContinueDraftReport = () => {
    const generalInformationDraft: GeneralInformationValueType | undefined =
      draftReport.current?.general_information;

    if (generalInformationDraft) {
      //Initialize Step 0
      if (!isEmpty(generalInformationDraft.device?.date))
        deviceDate.current = generalInformationDraft.device?.date;
      if (!isEmpty(generalInformationDraft.device?.coords))
        setDeviceLocation(generalInformationDraft.device?.coords);
      setDeviceDuration(generalInformationDraft.device?.duration as number);
      //Initialize Step 1
      setIncidentNumber(generalInformationDraft.incident_number as string);
      if (
        generalInformationDraft.state_related &&
        generalInformationDraft.state_related.to_connect_reports &&
        generalInformationDraft.state_related.to_connect_reports
          .event_identifier
      ) {
        setCaseNumber(
          generalInformationDraft?.state_related.to_connect_reports
            .event_identifier as string
        );
      }
      //Initialize Step 2
      setStep(2);
      setShowAssistant(false);
    }
    setContinueReportIsModalOpen(false);
  };

  const onAmendFormSubmit = async (contactValue: ReportValue) => {
    try {
      setIsLoading(true);
      const amendResponse: SaveReportResponse = await saveAmendReport(
        user?.email as string,
        {
          ...contactValue,
          created_in_version: process.env.REACT_APP_CC_VERSION
        }
      );

      setIsSubmitted(amendResponse.success);
      setIsSessionExpire(amendResponse.detail === 'Unauthorized');
      // Update Integration reports
      if (isOnline) {
        const integrationReports: ReportValue[] =
          await getReportsFromIntegration();
        setIntegrationReports(integrationReports);
      } else {
        setIntegrationReports([]);
      }
      setStep(3);
      setShowAssistant(true);
      setIsLoading(false);
      deleteDraftReport();
    } catch (e) {
      console.error('Report::AmendReport::onSubmit');
      handleError(e, history);
    }
  };

  const onContactFormSubmit = async (
    form: ReportValue,
    incidentNumber: string
  ) => {
    const newForm: ReportValue = { ...form };
    newForm.general_information.incident_number = incidentNumber;
    newForm.general_information.device = {
      coords: deviceLocation,
      duration: deviceDuration,
      date: deviceDate.current as string
    };
    newForm.created_in_version = process.env.REACT_APP_CC_VERSION;

    setIsLoading(true);
    try {
      const saveResponse: SaveReportResponse = await saveReport(
        user?.email as string,
        newForm
      );

      setIsSubmitted(saveResponse.success);
      setIsSessionExpire(saveResponse.detail === 'Unauthorized');

      // Update Integration reports
      if (isOnline) {
        const integrationReports: ReportValue[] =
          await getReportsFromIntegration();
        setIntegrationReports(integrationReports);
      } else {
        setIntegrationReports([]);
      }
      setStep(3);
      setShowAssistant(true);
      setIsLoading(false);
      deleteDraftReport();
    } catch (e) {
      const error: ApiError = e as ApiError;
      console.error('ReportWizard::CreateReport', error);

      if (error.detail === 'CREATED_REPORT_ALREADY_EXISTS') {
        setIsLoading(false);
        setIsIncidentNumberModalOpen(true);
      } else {
        handleError(e, history);
      }
    }
  };

  const onStartNewReport = () => {
    setIncidentNumber('');
    setCaseNumber('');
    setSubmittedForm(undefined);
    setDeviceDuration(0);
    setStep(1);
    setShowAssistant(false);
    history.push('/app/report');
  };

  const onCancel = () => {
    setCancelIsModalOpen(true);
  };

  const onModalClose = () => {
    setCancelIsModalOpen(false);
    deleteDraftReport();
  };

  const onDiscardChanges = () => {
    setShowAssistant(false);
    setCancelIsModalOpen(false);
    deleteDraftReport();
    onStartNewReport();
  };

  const onIncidentNumberModalClick = (newIncidentNumber: string) => {
    setIncidentNumber(newIncidentNumber);
    setIsIncidentNumberModalOpen(false);
    onContactFormSubmit(submittedForm as ReportValue, newIncidentNumber);
  };

  return (
    <Fragment>
      {(isLoading || !stateConfig) && <Loader />}
      {!isLoading && stateConfig && (
        <div className={styles.reportWizard}>
          {step === 1 && (
            <IncidentNumberView
              onCancel={onCancel}
              onContinue={onIncidentContinue}
              onCleanUp={onIncidentNumberCleanUp}
            />
          )}

          {step === 2 && (
            <DeviceLocationContext.Provider value={deviceLocation}>
              <ContactFormView
                isDraft={!!draftReport.current}
                report={draftReport.current}
                caseNumber={caseNumber}
                generalFormAreas={generalFormAreas}
                deviceDuration={deviceDuration}
                onCancel={onCancel}
                onSubmit={(contactValue: ReportValue) => {
                  //TODO: Workaround for amend report
                  //For any reason, the incident number is not being set correctly
                  contactValue.general_information.incident_number =
                    draftReport.current?.general_information.incident_number;

                  setSubmittedForm({ ...contactValue });
                  //Check for already created report
                  if (!!contactValue.general_information.created_by_user) {
                    onAmendFormSubmit({ ...contactValue });
                  } else {
                    onContactFormSubmit({ ...contactValue }, incidentNumber);
                  }
                }}
                onCleanUp={(contactValue: ReportValue) =>
                  saveDraftReport(contactValue)
                }
              />
            </DeviceLocationContext.Provider>
          )}

          {step === 3 && (
            <ReportSubmittedView
              isSubmitted={isSubmitted}
              isSessionExpire={isSessionExpire}
              incidentNumber={incidentNumber}
              eventIdentifier={
                submittedForm?.general_information.state_related
                  ?.to_connect_reports?.event_identifier as string
              }
              onStartNewReport={onStartNewReport}
            />
          )}

          <CancelModal
            isOpen={isCancelModalOpen}
            onDiscard={onDiscardChanges}
            onClose={onModalClose}
          />

          <ContinueReportModal
            isOpen={isContinueReportModalOpen}
            onDiscard={onDiscardDraftReport}
            onContinue={onContinueDraftReport}
          />

          <IncidentNumberModal
            value={incidentNumber}
            isOpen={isIncidentNumberModalOpen}
            onClick={onIncidentNumberModalClick}
          />
        </div>
      )}
    </Fragment>
  );
};

export default ReportWizard;
